import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  MemberNameCell,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { RowViewLink } from '@/components/common/RowActions';
import { formatNumber } from '@/utils/number';
import type { SystemDashboardRow, SystemDashboardSearch } from '../types/dashboard.types';

const numberCell = (value: number) => <span className="tabular-nums">{formatNumber(value)}</span>;

export function getSystemDashboardColumns(
  dashboardSearch: SystemDashboardSearch,
): ColumnDef<SystemDashboardRow, unknown>[] {
  return [
    createSerialColumn<SystemDashboardRow>(),
    {
      accessorKey: 'name',
      header: 'ZM Name / ID (Cell)',
      cell: ({ row }) => <MemberNameCell member={row.original} />,
    },
    {
      accessorKey: 'amName',
      header: 'AM Name',
      cell: ({ row }) => row.original.amName ?? <EmptyValue />,
    },
    {
      accessorKey: 'departmentName',
      header: 'Department',
      cell: ({ row }) => row.original.departmentName ?? '—',
    },
    {
      id: 'area',
      header: 'Division / District / Thana',
      cell: ({ row }) => (
        <span className="text-sm">
          {[row.original.divisionName, row.original.districtName, row.original.thanaName]
            .filter(Boolean)
            .join(', ') || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'dcrEntered',
      header: 'DCR Entered',
      cell: ({ row }) => numberCell(row.original.dcrEntered),
    },
    {
      accessorKey: 'dcrApproved',
      header: 'DCR Approved',
      cell: ({ row }) => numberCell(row.original.dcrApproved),
    },
    {
      accessorKey: 'derEntered',
      header: 'DER Entered',
      cell: ({ row }) => numberCell(row.original.derEntered),
    },
    {
      accessorKey: 'derApproved',
      header: 'DER Approved',
      cell: ({ row }) => numberCell(row.original.derApproved),
    },
    {
      accessorKey: 'dcrClaimedAmount',
      header: 'DCR Claimed (Tk)',
      cell: ({ row }) => numberCell(row.original.dcrClaimedAmount),
    },
    {
      accessorKey: 'dcrApprovedAmount',
      header: 'DCR Approved (Tk)',
      cell: ({ row }) => numberCell(row.original.dcrApprovedAmount),
    },
    {
      accessorKey: 'derClaimedAmount',
      header: 'DER Claimed (Tk)',
      cell: ({ row }) => numberCell(row.original.derClaimedAmount),
    },
    {
      accessorKey: 'derApprovedAmount',
      header: 'DER Approved (Tk)',
      cell: ({ row }) => numberCell(row.original.derApprovedAmount),
    },
    {
      accessorKey: 'receivedQty',
      header: 'SD Received',
      cell: ({ row }) => numberCell(row.original.receivedQty),
    },
    {
      accessorKey: 'returnedQty',
      header: 'SD Returned',
      cell: ({ row }) => numberCell(row.original.returnedQty),
    },
    {
      accessorKey: 'deliveredQty',
      header: 'SD Delivered',
      cell: ({ row }) => numberCell(row.original.deliveredQty),
    },
    {
      accessorKey: 'instituteCount',
      header: 'Unique Institutions',
      cell: ({ row }) => numberCell(row.original.instituteCount),
    },
    {
      accessorKey: 'teacherCount',
      header: 'Unique Teachers',
      cell: ({ row }) => numberCell(row.original.teacherCount),
    },
    createActionsColumn<SystemDashboardRow>((member) => (
      <RowViewLink
        to="/dashboards/system/$userId"
        params={{ userId: member.userId }}
        search={dashboardSearch}
        label="Summary"
        aria-label={`Work summary of ${member.name}`}
      />
    )),
  ];
}
